import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { http, fmtINR, fmtNum, fmtPct } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import ExplainableAiCard from "@/components/ExplainableAiCard";
import {
    ResponsiveContainer,
    Area,
    AreaChart,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { AlertTriangle, ArrowRight, TrendingUp, Package, Store } from "lucide-react";

const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function Dashboard() {
    const { openCopilot } = useOutletContext();
    const [kpis, setKpis] = useState(null);
    const [trend, setTrend] = useState([]);
    const [regionMix, setRegionMix] = useState([]);
    const [brandMix, setBrandMix] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        Promise.all([
            http.get("/dashboard/kpis").then((r) => setKpis(r.data)),
            http.get("/sellout/trend", { params: { days: 30 } }).then((r) => setTrend(r.data)),
            http.get("/dashboard/region-mix").then((r) => setRegionMix(r.data)),
            http.get("/dashboard/brand-mix").then((r) => setBrandMix(r.data)),
            http.get("/targets/leaderboard").then((r) => setLeaderboard(r.data)),
            http.get("/alerts", { params: { status: "open" } }).then((r) => setAlerts(r.data)),
        ]).catch(() => {});
    }, []);

    return (
        <div className="mx-auto max-w-[1560px] space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col gap-1">
                <div className="mlabel">Executive Home</div>
                <div className="flex items-end justify-between">
                    <h1 className="text-[26px] font-semibold tracking-tight">Network Command Center</h1>
                    <div className="text-[11px] text-muted-foreground">
                        Live · {kpis ? `${kpis.target_period}` : "…"}
                    </div>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                <KpiCard
                    testid="kpi-sellout"
                    label="Sell-Out · 30d"
                    value={kpis ? fmtINR(kpis.sellout_value_30d) : "…"}
                    delta={kpis?.sellout_delta_pct}
                    deltaLabel="vs prev 30d"
                    hint="Across all channels"
                />
                <KpiCard
                    testid="kpi-sellin"
                    label="Sell-In · 30d"
                    value={kpis ? fmtINR(kpis.sellin_value_30d) : "…"}
                    hint={`${fmtNum(kpis?.sellin_orders_30d)} orders`}
                />
                <KpiCard
                    testid="kpi-target"
                    label="Target Achievement"
                    value={kpis ? fmtPct(kpis.target_achievement_pct) : "…"}
                    hint={kpis ? `${fmtINR(kpis.target_achieved)} / ${fmtINR(kpis.target_total)}` : "…"}
                />
                <KpiCard
                    testid="kpi-stockouts"
                    label="Stock-outs"
                    value={kpis ? fmtNum(kpis.stockouts) : "…"}
                    hint={`${fmtNum(kpis?.dead_stock_positions)} dead-stock positions`}
                />
                <KpiCard
                    testid="kpi-store-health"
                    label="Store Health"
                    value={kpis ? kpis.avg_store_health : "…"}
                    hint="Average network score"
                    tone="ai"
                />
                <KpiCard
                    testid="kpi-alerts"
                    label="Open Alerts"
                    value={kpis ? fmtNum(kpis.open_alerts) : "…"}
                    hint={`${fmtNum(kpis?.critical_alerts)} critical`}
                />
            </div>

            {/* Row: trend + AI insights */}
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 rounded-sm border border-border bg-card p-4 lg:col-span-8">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="mlabel">Sell-Out Velocity</div>
                            <div className="text-sm font-semibold">Daily value · 30 days</div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <TrendingUp className="h-3.5 w-3.5" /> Live from POS + CSV + API sources
                        </div>
                    </div>
                    <div className="h-[260px]" data-testid="chart-sellout-trend">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="sob" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(d) => d.slice(5)}
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={10}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={10}
                                    tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v)}
                                    width={40}
                                    tickLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: 4,
                                        fontSize: 12,
                                    }}
                                    formatter={(v) => fmtINR(v)}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="hsl(var(--chart-1))"
                                    strokeWidth={2}
                                    fill="url(#sob)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="col-span-12 space-y-3 lg:col-span-4">
                    <ExplainableAiCard
                        testid="ai-card-replenish"
                        title="Replenishment Opportunity"
                        reasoning="12 A-tier stores in South & West show sell-out velocity 22% above 30-day baseline while on-hand cover has dropped below 3 weeks. Suggested inter-warehouse transfer to close the gap before weekend footfall."
                        confidence={0.86}
                        impact="+ ₹18.4 L in 14 days"
                        action="Review 12 suggested transfers"
                        onApply={openCopilot}
                    />
                    <ExplainableAiCard
                        testid="ai-card-deadstock"
                        title="Dead-stock Liquidation"
                        reasoning="150 store-SKU positions classified Dead have zero movement in 60+ days. Consolidating into a Home Bundle scheme could recover ~35% of tied-up capital."
                        confidence={0.72}
                        impact="Free ₹42 L working capital"
                        action="Draft liquidation scheme"
                        onApply={openCopilot}
                    />
                </div>
            </div>

            {/* Row: Region mix + Brand mix + Leaderboard */}
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 rounded-sm border border-border bg-card p-4 md:col-span-4">
                    <div className="mlabel mb-3">Region Mix · 30d</div>
                    <div className="h-[220px]" data-testid="chart-region-mix">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={regionMix} layout="vertical" margin={{ left: 10, right: 10 }}>
                                <XAxis type="number" hide />
                                <YAxis
                                    type="category"
                                    dataKey="region"
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={11}
                                    width={60}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        fontSize: 12,
                                    }}
                                    formatter={(v) => fmtINR(v)}
                                />
                                <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[0, 3, 3, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="col-span-12 rounded-sm border border-border bg-card p-4 md:col-span-4">
                    <div className="mlabel mb-3">Brand Mix · 30d</div>
                    <div className="h-[220px]" data-testid="chart-brand-mix">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Tooltip
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        fontSize: 12,
                                    }}
                                    formatter={(v) => fmtINR(v)}
                                />
                                <Pie
                                    data={brandMix}
                                    dataKey="value"
                                    nameKey="brand"
                                    innerRadius={45}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {brandMix.map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-2 space-y-1">
                        {brandMix.map((b, i) => (
                            <div key={b.brand} className="flex items-center justify-between text-[11px]">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="h-2 w-2 rounded-full"
                                        style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                                    />
                                    {b.brand}
                                </div>
                                <span className="mono-num text-muted-foreground">{fmtINR(b.value)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-span-12 rounded-sm border border-border bg-card p-4 md:col-span-4">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="mlabel">Top Stores · Target Achievement</div>
                        <Link
                            to="/targets"
                            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                            data-testid="link-view-targets"
                        >
                            View all <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                    <div className="space-y-2" data-testid="leaderboard">
                        {leaderboard.slice(0, 6).map((r, i) => (
                            <div key={r.store_id} className="rounded-sm bg-muted/40 p-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="mono-num text-[10px] text-muted-foreground">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <div className="text-[12px] font-medium">{r.store}</div>
                                    </div>
                                    <span
                                        className={
                                            "mono-num text-[12px] font-semibold " +
                                            (r.pct >= 100
                                                ? "text-emerald-600"
                                                : r.pct >= 80
                                                ? "text-foreground"
                                                : "text-red-500")
                                        }
                                    >
                                        {fmtPct(r.pct)}
                                    </span>
                                </div>
                                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-border">
                                    <div
                                        className={
                                            "h-full " +
                                            (r.pct >= 100
                                                ? "bg-emerald-500"
                                                : r.pct >= 80
                                                ? "bg-primary"
                                                : "bg-red-400")
                                        }
                                        style={{ width: `${Math.min(100, r.pct)}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Alerts feed */}
            <div className="rounded-sm border border-border bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <div className="mlabel">Action Queue</div>
                        <div className="text-sm font-semibold">Open alerts across the network</div>
                    </div>
                    <Link
                        to="/alerts"
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                        data-testid="link-view-alerts"
                    >
                        Alert center <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="alerts-feed">
                    {alerts.slice(0, 6).map((a) => (
                        <div
                            key={a.id}
                            className="hover-lift rounded-sm border border-border p-3"
                            data-testid={`alert-${a.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-1.5">
                                    <SeverityDot sev={a.severity} />
                                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                        {a.category.replace("_", " ")}
                                    </div>
                                </div>
                                <div className="text-[10px] uppercase text-muted-foreground">{a.severity}</div>
                            </div>
                            <div className="mt-1 text-[13px] font-medium">{a.title}</div>
                            <div className="mt-1 text-[12px] text-muted-foreground">{a.detail}</div>
                            {a.ai_confidence != null && (
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-[hsl(var(--ai))]">
                                        AI conf
                                    </span>
                                    <div className="conf-bar flex-1">
                                        <span style={{ width: `${Math.round(a.ai_confidence * 100)}%` }} />
                                    </div>
                                    <span className="mono-num text-[10px]">{Math.round(a.ai_confidence * 100)}%</span>
                                </div>
                            )}
                        </div>
                    ))}
                    {alerts.length === 0 && (
                        <div className="col-span-full text-[12px] text-muted-foreground">
                            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> No open alerts. Network is stable.
                        </div>
                    )}
                </div>
            </div>

            {/* Quick nav footer */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <QuickCard
                    to="/inventory"
                    icon={Package}
                    label="Inventory Ledger"
                    hint="Store-SKU positions, ageing & health"
                    testid="quick-inventory"
                />
                <QuickCard
                    to="/sellout"
                    icon={TrendingUp}
                    label="Sell-Out Data Hub"
                    hint="Ingest, reconcile & explore"
                    testid="quick-sellout"
                />
                <QuickCard
                    to="/masters/stores"
                    icon={Store}
                    label="Distribution Network"
                    hint="Distributors, MBOs, health scores"
                    testid="quick-stores"
                />
            </div>
        </div>
    );
}

function SeverityDot({ sev }) {
    const c =
        sev === "critical"
            ? "bg-red-500"
            : sev === "high"
            ? "bg-orange-500"
            : sev === "medium"
            ? "bg-amber-400"
            : "bg-slate-400";
    return <span className={`inline-block h-1.5 w-1.5 rounded-full ${c}`} />;
}

function QuickCard({ to, icon: Icon, label, hint, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="hover-lift group flex items-center gap-3 rounded-sm border border-border bg-card p-4"
        >
            <div className="grid h-9 w-9 place-items-center rounded-sm bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
                <div className="text-sm font-semibold">{label}</div>
                <div className="text-[11px] text-muted-foreground">{hint}</div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
    );
}
